import { useState } from 'react';
import { X, AlertOctagon, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface ReportProblemModalProps {
  onClose: () => void;
}

export function ReportProblemModal({ onClose }: ReportProblemModalProps) {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    category: 'bug' as 'bug' | 'feature' | 'question' | 'other'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create ticket in database
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .insert({
          user_id: profile?.id,
          user_email: profile?.email,
          user_name: profile?.full_name,
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          category: formData.category,
          status: 'open'
        })
        .select()
        .single();

      if (ticketError) throw ticketError;

      // Notify support by email through the server-side send-email function
      // (Resend). Best-effort: the ticket is already saved if this fails.
      try {
        const { sendEmail } = await import('../../services/emailService');
        await sendEmail({
          to: 'marketing@viceseo.com',
          subject: `[TMS Support] Ticket #${ticket.id} - ${formData.title}`,
          fromName: 'CS NATIONAL TMS',
          html: `
            <h2>New Support Ticket #${ticket.id}</h2>
            <p><strong>From:</strong> ${profile?.full_name} (${profile?.email})</p>
            <p><strong>Priority:</strong> ${formData.priority.toUpperCase()}</p>
            <p><strong>Category:</strong> ${formData.category}</p>
            <p><strong>Title:</strong> ${formData.title}</p>
            <p><strong>Description:</strong></p>
            <p>${formData.description}</p>
            <hr>
            <p><a href="${window.location.origin}/support-tickets">View Ticket in TMS</a></p>
          `,
        });
      } catch (emailErr) {
        console.error('Support notification email failed (ticket still saved):', emailErr);
      }

      alert('Your problem report has been submitted successfully! We will get back to you soon.');
      onClose();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      alert('Failed to submit problem report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <AlertOctagon className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Report a Problem</h2>
              <p className="text-sm text-slate-600">We'll get back to you as soon as possible</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            >
              <option value="bug">Bug / Error</option>
              <option value="feature">Feature Request</option>
              <option value="question">Question / Help</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Priority *
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, priority: 'low' })}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.priority === 'low'
                    ? 'border-green-600 bg-green-50 text-green-900'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                Low
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, priority: 'medium' })}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.priority === 'medium'
                    ? 'border-yellow-600 bg-yellow-50 text-yellow-900'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                Medium
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, priority: 'high' })}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.priority === 'high'
                    ? 'border-red-600 bg-red-50 text-red-900'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                High
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Problem Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Brief description of the problem"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Detailed Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[150px]"
              placeholder="Please provide as much detail as possible about the problem..."
              required
            />
            <p className="text-xs text-slate-500 mt-2">
              Include steps to reproduce, error messages, and any other relevant information
            </p>
          </div>

          {/* User Info Display */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Your Information:</strong>
            </p>
            <p className="text-sm text-slate-700 mt-1">
              Name: {profile?.full_name}
            </p>
            <p className="text-sm text-slate-700">
              Email: {profile?.email}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
